import { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Mail, Phone, Calendar, User, MessageSquare, Sparkles, ArrowLeft, LogOut, Download, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "malalaka_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("contacts");
  const [data, setData] = useState({ contacts: [], quiz: [], newsletter: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Espace Admin · Gwladys Malalaka";
    if (token) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const tryLogin = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Token": input } });
      sessionStorage.setItem(TOKEN_KEY, input);
      setToken(input);
    } catch {
      setErr("Mot de passe incorrect.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInput("");
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const h = { headers: { "X-Admin-Token": token } };
      const [c, q, n] = await Promise.all([
        axios.get(`${API}/admin/contacts`, h),
        axios.get(`${API}/admin/quiz`, h),
        axios.get(`${API}/admin/newsletter`, h),
      ]);
      setData({ contacts: c.data.items, quiz: q.data.items, newsletter: n.data.items });
    } catch {
      setErr("Erreur de chargement. Veuillez vous reconnecter.");
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-cream grid place-items-center p-6" data-testid="admin-login">
        <form onSubmit={tryLogin} className="w-full max-w-sm bg-ivory rounded-3xl border border-ink-soft p-8 space-y-5">
          <div className="flex items-center gap-2 text-coral">
            <Lock size={18} /> <p className="text-xs uppercase tracking-widest">Espace privé</p>
          </div>
          <h1 className="font-display text-2xl text-deep-teal">Connexion Gwladys</h1>
          <p className="text-sm text-ink/70">Entrez votre mot de passe privé pour accéder à vos contacts, quiz et newsletter.</p>
          <input
            type="password"
            required
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Mot de passe"
            className="w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none"
            data-testid="admin-password-input"
          />
          {err && <p className="text-sm text-red-600" data-testid="admin-error">{err}</p>}
          <button type="submit" className="btn-primary w-full" data-testid="admin-login-submit">Entrer</button>
          <a href="/" className="block text-center text-sm text-ink/60 hover:text-deep-teal" data-testid="admin-back">← Retour au site</a>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-deep-teal text-ivory py-6 px-6 lg:px-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-coral-soft" style={{color:"#E8A78D"}}>Espace privé</p>
            <h1 className="font-display text-2xl">Bonjour Gwladys 🌿</h1>
          </div>
          <div className="flex gap-3">
            <a href="/" className="text-sm text-ivory/80 hover:text-coral inline-flex items-center gap-1" data-testid="admin-home-link"><ArrowLeft size={14}/> Site</a>
            <button onClick={loadAll} className="text-sm text-ivory/80 hover:text-coral" data-testid="admin-refresh">Actualiser</button>
            <button onClick={logout} className="text-sm text-ivory/80 hover:text-coral inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={14}/> Déconnexion</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="p-5 rounded-2xl bg-ivory border border-ink-soft" data-testid="stat-contacts">
            <p className="text-xs uppercase tracking-widest text-coral">Messages contact</p>
            <p className="mt-2 font-display text-4xl text-deep-teal">{data.contacts.length}</p>
          </div>
          <div className="p-5 rounded-2xl bg-ivory border border-ink-soft" data-testid="stat-quiz">
            <p className="text-xs uppercase tracking-widest text-coral">Quiz remplis</p>
            <p className="mt-2 font-display text-4xl text-deep-teal">{data.quiz.length}</p>
          </div>
          <div className="p-5 rounded-2xl bg-ivory border border-ink-soft" data-testid="stat-newsletter">
            <p className="text-xs uppercase tracking-widest text-coral">Abonnées newsletter</p>
            <p className="mt-2 font-display text-4xl text-deep-teal">{data.newsletter.length}</p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-ink-soft mb-6">
          {[
            { k: "contacts", label: "Messages", icon: MessageSquare },
            { k: "quiz", label: "Quiz", icon: Sparkles },
            { k: "newsletter", label: "Newsletter", icon: Mail },
            { k: "documents", label: "Mes documents", icon: FileText },
          ].map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors ${tab === t.k ? "border-coral text-deep-teal font-medium" : "border-transparent text-ink/60 hover:text-deep-teal"}`}
              data-testid={`admin-tab-${t.k}`}
            >
              <t.icon size={16}/> {t.label}
            </button>
          ))}
        </div>

        {loading && <p className="text-ink/60 italic">Chargement…</p>}

        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {data.contacts.length === 0 && <p className="text-ink/60 italic">Aucun message pour le moment.</p>}
            {data.contacts.map((c, i) => (
              <article key={c.id || i} className="p-5 rounded-2xl bg-ivory border border-ink-soft" data-testid={`contact-row-${i}`}>
                <div className="flex flex-wrap items-center gap-3 text-sm text-ink/70 mb-2">
                  <span className="inline-flex items-center gap-1"><Calendar size={12}/> {fmtDate(c.created_at)}</span>
                  <span className="px-2 py-0.5 rounded-full bg-sage-soft/40 text-xs text-deep-teal">{c.sujet}</span>
                </div>
                <p className="font-display text-lg text-deep-teal inline-flex items-center gap-2"><User size={14}/> {c.nom}</p>
                <div className="mt-2 flex flex-wrap gap-4 text-sm">
                  <a href={`mailto:${c.email}`} className="text-deep-teal underline inline-flex items-center gap-1"><Mail size={12}/> {c.email}</a>
                  {c.telephone && <a href={`tel:${c.telephone}`} className="text-deep-teal underline inline-flex items-center gap-1"><Phone size={12}/> {c.telephone}</a>}
                </div>
                <p className="mt-3 text-ink/80 text-sm whitespace-pre-wrap">{c.message}</p>
              </article>
            ))}
          </div>
        )}

        {tab === "quiz" && (
          <div className="space-y-3" data-testid="admin-quiz-list">
            {data.quiz.length === 0 && <p className="text-ink/60 italic">Aucun quiz rempli pour le moment.</p>}
            {data.quiz.map((q, i) => (
              <article key={q.id || i} className="p-5 rounded-2xl bg-ivory border border-ink-soft" data-testid={`quiz-row-${i}`}>
                <div className="flex flex-wrap items-center gap-3 text-sm text-ink/70 mb-2">
                  <span className="inline-flex items-center gap-1"><Calendar size={12}/> {fmtDate(q.created_at)}</span>
                  <span className="px-2 py-0.5 rounded-full bg-coral/20 text-xs text-coral font-medium">Module : {q.module}</span>
                </div>
                <a href={`mailto:${q.email}`} className="text-deep-teal underline text-sm inline-flex items-center gap-1"><Mail size={12}/> {q.email}</a>
                {q.answers && q.answers.length > 0 && (
                  <ul className="mt-3 space-y-1 text-sm text-ink/80">
                    {q.answers.map((a, j) => (
                      <li key={j}>• {a.question} → <em className="text-deep-teal">{a.module}</em></li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="space-y-2" data-testid="admin-newsletter-list">
            {data.newsletter.length === 0 && <p className="text-ink/60 italic">Aucune inscription pour le moment.</p>}
            {data.newsletter.map((n, i) => (
              <div key={n.id || i} className="p-3 rounded-xl bg-ivory border border-ink-soft flex items-center justify-between" data-testid={`newsletter-row-${i}`}>
                <a href={`mailto:${n.email}`} className="text-deep-teal underline text-sm inline-flex items-center gap-2"><Mail size={14}/> {n.email}</a>
                <span className="text-xs text-ink/60">{fmtDate(n.created_at)}</span>
              </div>
            ))}
          </div>
        )}

        {tab === "documents" && (
          <div className="space-y-3" data-testid="admin-documents-list">
            <p className="text-sm text-ink/70 mb-4">🔒 Vos documents privés — accès sécurisé par mot de passe. Cliquez pour télécharger.</p>
            {[
              { file: "PITCH_DECK_RENAISSANCE.pptx", title: "🎨 Pitch deck 5 slides — Renaissance", desc: "PowerPoint — Présentation vitrine pour expert·es (sans dévoiler la méthode) ⭐ KIT PROTECTION" },
              { file: "NDA_MODELE.docx", title: "⚖️ Modèle NDA (Accord de confidentialité)", desc: "Word — À faire signer avant tout RDV stratégique (1 page, prêt à imprimer) ⭐ KIT PROTECTION" },
              { file: "MAILS_APPROCHE_EXPERTS.docx", title: "📧 Mails-types — Formateur Boclet & Théo Duverger", desc: "Word — 3 mails prêts à personnaliser + posture pro à adopter ⭐ KIT PROTECTION" },
              { file: "GUIDE_PROTECTION_RDV_TIERS.docx", title: "🛡️ Guide protection RDV avec des tiers", desc: "Word — 5 règles d'or + grille de risque + checklist + phrases à dire/éviter ⭐ KIT PROTECTION" },
              { file: "FORMATION_RENAISSANCE_BURNOUT.docx", title: "🌸 Formation phare — Renaissance après burn-out", desc: "Word — 6 piliers + programme 10 semaines + tarifs + supports à créer" },
              { file: "CALCUL_SOCLE_ECO_TJM.xlsx", title: "💰 Calcul socle économique & TJM", desc: "Excel — Hypothèses, charges, calcul TJM, 3 gammes tarifaires, alternatives Adobe" },
              { file: "ANALYSE_POSITIONNEMENT_PROSPECTS.docx", title: "🎯 Analyse positionnement & 30 prospects B2B", desc: "Word — Marché Limoges, vos forces, liste de prospects, template mail B2B" },
              { file: "CATALOGUE_FORMATIONS_GWLADYS.docx", title: "📚 Catalogue de 8 formations + 18 théoriciens", desc: "Word — Programmes détaillés, objectifs SMART, tarifs, plan J0-J360" },
              { file: "GUIDE_GOOGLE_BUSINESS_PROFILE.docx", title: "🏢 Guide Google Business Profile", desc: "Word — Procédure 11 étapes + stratégie avis" },
              { file: "GUIDE_LINKEDIN_FACEBOOK_POSTS.docx", title: "📱 Guide LinkedIn + Facebook Pro + 10 posts", desc: "Word — Création comptes pro + 10 posts chocs culturels prêts" },
              { file: "GUIDE_PROTECTION_COPYRIGHT.docx", title: "🛡️ Guide protection copyright (général)", desc: "Word — Archive.org + INPI e-Soleau + SGDL" },
            ].map((d, i) => (
              <a
                key={i}
                href={`${API}/admin/documents/${d.file}?token=${encodeURIComponent(token)}`}
                download
                className="block p-5 rounded-2xl bg-ivory border border-ink-soft hover:border-deep-teal hover:bg-cream transition-colors group"
                data-testid={`document-${i}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-deep-teal text-cream grid place-items-center group-hover:bg-coral transition-colors">
                    <Download size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-lg text-deep-teal">{d.title}</p>
                    <p className="text-sm text-ink/70 mt-1">{d.desc}</p>
                    <p className="text-xs text-coral mt-1 font-mono">{d.file}</p>
                  </div>
                </div>
              </a>
            ))}
            <p className="text-xs text-ink/50 mt-6 italic">
              🔒 Ces documents sont stockés hors du site public et ne sont accessibles qu'avec votre mot de passe. Personne d'autre ne peut y accéder, même en devinant l'URL.
            </p>
          </div>
        )}

        <p className="mt-12 text-xs text-ink/50 text-center">Exportez vos données en copiant-collant dans un tableur, ou demandez une extraction complète.</p>
      </div>
    </main>
  );
};

export default Admin;
